import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createCase,
  deleteCase,
  listCases,
  setCaseTags,
  updateCase,
} from './casesAdminApi'
import {
  EvalCaseCreateInput,
  EvalCaseTagsUpdateInput,
  EvalCaseUpdateInput,
} from './casesAdminModel'

const ADMIN_LIST_KEY = ['casesAdmin', 'list'] as const

export const useCasesListQuery = () => {
  return useQuery({
    queryKey: ADMIN_LIST_KEY,
    queryFn: () => listCases(),
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load cases' })
    },
  })
}

export const useCreateCaseMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: EvalCaseCreateInput) => createCase(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Case created' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to create case', description })
    },
  })
}

export const useUpdateCaseMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: EvalCaseUpdateInput) => updateCase(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Case updated' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to update case', description })
    },
  })
}

export const useSetCaseTagsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: EvalCaseTagsUpdateInput) => setCaseTags(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Tags updated' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to update tags', description })
    },
  })
}

export const useDeleteCaseMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((caseId: string) => deleteCase(caseId), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Case deleted' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to delete case', description })
    },
  })
}
